import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Grid,
  Stepper,
  Step,
  StepLabel,
  FormHelperText,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Save as SaveIcon,
  ArrowBack as BackIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Description as DescriptionIcon,
  AttachMoney as MoneyIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { budgetAPI } from "../../services/api/budget.api";
import { usePermissions } from "../../hooks/auth/usePermissions";

const steps = ["Budget Period", "Activity Details", "Budget Amount"];

const BudgetManagementPage = () => {
  const navigate = useNavigate();
  const { user } = usePermissions();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [periods, setPeriods] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedBudget, setSelectedBudget] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    budget_period_id: "",
    activity_name: "",
    responsible_person_id: "",
    description: "",
    amount: "",
  });
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    fetchBudgetPeriods();
    fetchBudgets();
  }, []);

  // Set responsible person when user data is available
  useEffect(() => {
    console.log("User data in CreateBudgetPage:", user);
    if (user && user.id) {
      setFormData((prev) => ({
        ...prev,
        responsible_person_id: user.id,
      }));
    }
  }, [user]);

  const fetchBudgetPeriods = async () => {
    try {
      const response = await budgetAPI.getBudgetPeriods({ status: "active" });
      if (response.data.success) {
        setPeriods(response.data.data.budgetPeriods || []);
      }
    } catch (error) {
      console.error("Error fetching budget periods:", error);
    }
  };

  const fetchBudgets = async () => {
    try {
      const response = await budgetAPI.getBudgets();
      if (response.data.success) {
        setBudgets(response.data.data.budgets || []);
      }
    } catch (error) {
      console.error("Error fetching budgets:", error);
    }
  };

  // Helper functions for budget management
  const getStatusColor = (status) => {
    switch (status) {
      case "draft":
        return "default";
      case "submitted":
        return "warning";
      case "finance_approved":
        return "info";
      case "finance_rejected":
        return "error";
      case "admin_approved":
        return "success";
      case "admin_rejected":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "draft":
        return "Draft";
      case "submitted":
        return "Submitted";
      case "finance_approved":
        return "Finance Approved";
      case "finance_rejected":
        return "Finance Rejected";
      case "admin_approved":
        return "Admin Approved";
      case "admin_rejected":
        return "Admin Rejected";
      default:
        return status;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-TZ", {
      style: "currency",
      currency: "TZS",
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 0:
        if (!formData.budget_period_id) {
          newErrors.budget_period_id = "Budget period is required";
        }
        break;
      case 1:
        if (!formData.activity_name) {
          newErrors.activity_name = "Activity name is required";
        }
        // Remove responsible person validation since it's auto-set
        break;
      case 2:
        if (!formData.amount || parseFloat(formData.amount) <= 0) {
          newErrors.amount = "Valid budget amount is required";
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(activeStep)) return;

    try {
      setLoading(true);

      if (editMode) {
        // Update existing budget
        await budgetAPI.updateBudget(selectedBudget.id, formData);
        setSelectedBudget(null);
        setEditMode(false);
        setSuccessMessage("Budget updated successfully!");
      } else {
        // Create new budget
        await budgetAPI.createBudget(formData);
        setShowCreateForm(false);
        setSuccessMessage("Budget created successfully!");
      }

      setActiveStep(0);
      setFormData({
        budget_period_id: "",
        activity_name: "",
        responsible_person_id: "",
        description: "",
        amount: "",
      });
      fetchBudgets(); // Refresh the budget list
    } catch (error) {
      console.error("Error saving budget:", error);

      // Handle different types of errors
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        console.log("Backend error response:", errorData);

        // Handle validation errors
        if (errorData.errors && Array.isArray(errorData.errors)) {
          const fieldErrors = {};
          errorData.errors.forEach((err) => {
            fieldErrors[err.field] = err.message;
          });
          setErrors(fieldErrors);
        }
        // Handle single error message
        else if (errorData.message) {
          setErrors({ submit: errorData.message });
        }
        // Handle generic error
        else {
          setErrors({ submit: "Failed to save budget. Please try again." });
        }
      } else {
        setErrors({
          submit: "Network error. Please check your connection and try again.",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditBudget = (budget) => {
    setSelectedBudget(budget);
    setEditMode(true);
    setFormData({
      budget_period_id: budget.budget_period_id || "",
      activity_name: budget.activity_name || "",
      responsible_person_id: budget.responsible_person_id || user?.id || "",
      description: budget.description || "",
      amount: budget.amount || "",
    });
    setActiveStep(0);
    setErrors({});
  };

  const handleSubmitBudget = async (budget) => {
    if (
      window.confirm(
        `Are you sure you want to submit "${budget.activity_name}" for Finance Manager approval? This action cannot be undone.`
      )
    ) {
      try {
        setLoading(true);
        await budgetAPI.submitBudget(budget.id);
        setSelectedBudget(null);
        setSuccessMessage(
          "Budget submitted successfully! It is now pending Finance Manager approval."
        );
        fetchBudgets();
      } catch (error) {
        console.error("Error submitting budget:", error);
        setErrors({ submit: "Failed to submit budget" });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleResubmitBudget = async (budget) => {
    try {
      setLoading(true);
      await budgetAPI.submitBudget(budget.id);
      setSelectedBudget(null);
      setSuccessMessage("Budget resubmitted successfully!");
      fetchBudgets();
    } catch (error) {
      console.error("Error resubmitting budget:", error);
      setErrors({ submit: "Failed to resubmit budget" });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBudget = async (budget) => {
    if (window.confirm("Are you sure you want to delete this budget?")) {
      try {
        setLoading(true);
        await budgetAPI.deleteBudget(budget.id);
        setSuccessMessage("Budget deleted successfully!");
        fetchBudgets();
      } catch (error) {
        console.error("Error deleting budget:", error);
        setErrors({ submit: "Failed to delete budget" });
      } finally {
        setLoading(false);
      }
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <BusinessIcon sx={{ mr: 1 }} />
                <Typography variant="h6">
                  {editMode
                    ? "Budget Period (Read-only)"
                    : "Select Budget Period"}
                </Typography>
              </Box>

              <FormControl fullWidth error={!!errors.budget_period_id}>
                <InputLabel>Budget Period</InputLabel>
                <Select
                  value={formData.budget_period_id}
                  label="Budget Period"
                  disabled={editMode}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      budget_period_id: e.target.value,
                    })
                  }
                >
                  {periods.map((period) => (
                    <MenuItem key={period.id} value={period.id}>
                      {period.name} ({period.period_type})
                    </MenuItem>
                  ))}
                </Select>
                {errors.budget_period_id && (
                  <FormHelperText>{errors.budget_period_id}</FormHelperText>
                )}
                {editMode && (
                  <FormHelperText>
                    Budget period cannot be changed once created
                  </FormHelperText>
                )}
              </FormControl>

              {formData.budget_period_id && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  {editMode
                    ? "This budget is associated with the selected period"
                    : "Selected period details will be shown here"}
                </Alert>
              )}
            </CardContent>
          </Card>
        );

      case 1:
        return (
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <DescriptionIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Activity Details</Typography>
              </Box>

              <TextField
                fullWidth
                label="Activity Name"
                value={formData.activity_name}
                onChange={(e) =>
                  setFormData({ ...formData, activity_name: e.target.value })
                }
                error={!!errors.activity_name}
                helperText={errors.activity_name}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Responsible Person"
                value={
                  user
                    ? user.first_name && user.last_name
                      ? `${user.first_name} ${user.last_name}`
                      : user.name ||
                        user.username ||
                        user.email ||
                        `User ID: ${user.id}`
                    : "Loading user data..."
                }
                InputProps={{
                  readOnly: true,
                }}
                sx={{ mb: 2 }}
                helperText={
                  user
                    ? "You (Department Head) are automatically set as the responsible person"
                    : "Loading..."
                }
              />

              <TextField
                fullWidth
                label="Description"
                multiline
                rows={4}
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Describe the activity, objectives, and expected outcomes..."
              />
            </CardContent>
          </Card>
        );

      case 2:
        return (
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <MoneyIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Budget Amount</Typography>
              </Box>

              <TextField
                fullWidth
                label="Budget Amount (TSh)"
                type="number"
                value={formData.amount}
                onChange={(e) =>
                  setFormData({ ...formData, amount: e.target.value })
                }
                error={!!errors.amount}
                helperText={
                  errors.amount ||
                  "Enter the total budget amount in Tanzanian Shillings"
                }
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>TSh</Typography>,
                }}
              />

              <Alert severity="info" sx={{ mt: 2 }}>
                This budget will be submitted for Finance Manager approval after
                creation.
              </Alert>
            </CardContent>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate(-1)}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4">Budget Management</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowCreateForm(true)}
        >
          Create New Budget
        </Button>
      </Box>

      {successMessage && (
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          onClose={() => setSuccessMessage("")}
        >
          {successMessage}
        </Alert>
      )}

      {/* Budget List */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6">Your Budgets</Typography>
            <Button
              startIcon={<RefreshIcon />}
              onClick={fetchBudgets}
              size="small"
            >
              Refresh
            </Button>
          </Box>

          {budgets.length === 0 ? (
            <Alert severity="info">
              No budgets found. Create your first budget to get started.
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Activity Name</TableCell>
                    <TableCell>Budget Period</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {budgets.map((budget) => (
                    <TableRow
                      key={budget.id}
                      onClick={() => setSelectedBudget(budget)}
                      sx={{
                        cursor: "pointer",
                        "&:hover": { backgroundColor: "action.hover" },
                      }}
                    >
                      <TableCell>{budget.activity_name}</TableCell>
                      <TableCell>
                        {budget.BudgetPeriod ? budget.BudgetPeriod.name : "N/A"}
                      </TableCell>
                      <TableCell>{formatCurrency(budget.amount)}</TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusText(budget.status)}
                          color={getStatusColor(budget.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{formatDate(budget.created_at)}</TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => setSelectedBudget(budget)}
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          {budget.status === "draft" && (
                            <Tooltip title="Submit Budget">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleSubmitBudget(budget)}
                              >
                                <SaveIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {(budget.status === "draft" ||
                            budget.status === "finance_rejected" ||
                            budget.status === "admin_rejected") && (
                            <Tooltip title="Edit Budget">
                              <IconButton
                                size="small"
                                onClick={() => handleEditBudget(budget)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {(budget.status === "finance_rejected" ||
                            budget.status === "admin_rejected") && (
                            <Tooltip title="Resubmit Budget">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleResubmitBudget(budget)}
                              >
                                <RefreshIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {budget.status === "draft" && (
                            <Tooltip title="Delete Budget">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteBudget(budget)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Create Budget Dialog */}
      <Dialog
        open={showCreateForm}
        onClose={() => setShowCreateForm(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">Create New Budget</Typography>
            <Button onClick={() => setShowCreateForm(false)}>Cancel</Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            Create a new budget for your department. You (Department Head) will
            automatically be set as the responsible person. The budget will be
            submitted for approval after creation.
          </Alert>

          {errors.submit && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {errors.submit}
            </Alert>
          )}

          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {renderStepContent(activeStep)}

          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
            <Button disabled={activeStep === 0} onClick={handleBack}>
              Back
            </Button>

            <Box>
              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading ? "Creating..." : "Create Budget"}
                </Button>
              ) : (
                <Button variant="contained" onClick={handleNext}>
                  Next
                </Button>
              )}
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Budget Details Dialog */}
      <Dialog
        open={!!selectedBudget && !editMode}
        onClose={() => setSelectedBudget(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">Budget Details</Typography>
            <Button onClick={() => setSelectedBudget(null)}>Close</Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedBudget && (
            <>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h5" gutterBottom>
                    {selectedBudget.activity_name}
                  </Typography>
                  <Chip
                    label={getStatusText(selectedBudget.status)}
                    color={getStatusColor(selectedBudget.status)}
                    size="medium"
                    sx={{ mb: 2 }}
                  />

                  {/* Rejection Alert */}
                  {(selectedBudget.finance_rejection_comment ||
                    selectedBudget.admin_rejection_comment) && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Budget Rejected
                      </Typography>
                      {selectedBudget.finance_rejection_comment && (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Finance Manager:</strong>{" "}
                          {selectedBudget.finance_rejection_comment}
                        </Typography>
                      )}
                      {selectedBudget.admin_rejection_comment && (
                        <Typography variant="body2">
                          <strong>Admin:</strong>{" "}
                          {selectedBudget.admin_rejection_comment}
                        </Typography>
                      )}
                    </Alert>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Budget Period
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedBudget.BudgetPeriod
                      ? selectedBudget.BudgetPeriod.name
                      : "N/A"}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Amount
                  </Typography>
                  <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
                    {formatCurrency(selectedBudget.amount)}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Created Date
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {formatDate(selectedBudget.created_at)}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Last Updated
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {formatDate(selectedBudget.updated_at)}
                  </Typography>
                </Grid>

                {selectedBudget.submitted_at && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      Submitted Date
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {formatDate(selectedBudget.submitted_at)}
                    </Typography>
                  </Grid>
                )}

                {selectedBudget.finance_approved_at && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      Finance Approved Date
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {formatDate(selectedBudget.finance_approved_at)}
                    </Typography>
                  </Grid>
                )}

                {selectedBudget.admin_approved_at && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      Admin Approved Date
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {formatDate(selectedBudget.admin_approved_at)}
                    </Typography>
                  </Grid>
                )}

                {selectedBudget.finance_rejected_at && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      Finance Rejected Date
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {formatDate(selectedBudget.finance_rejected_at)}
                    </Typography>
                  </Grid>
                )}

                {selectedBudget.admin_rejected_at && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      Admin Rejected Date
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {formatDate(selectedBudget.admin_rejected_at)}
                    </Typography>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Description
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedBudget.description || "No description provided"}
                  </Typography>
                </Grid>

                {selectedBudget.finance_approval_comment && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Finance Approval Comment
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {selectedBudget.finance_approval_comment}
                    </Typography>
                  </Grid>
                )}

                {selectedBudget.admin_approval_comment && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Admin Approval Comment
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {selectedBudget.admin_approval_comment}
                    </Typography>
                  </Grid>
                )}

                {selectedBudget.finance_rejection_comment && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Finance Rejection Reason
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }} color="error">
                      {selectedBudget.finance_rejection_comment}
                    </Typography>
                  </Grid>
                )}

                {selectedBudget.admin_rejection_comment && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Admin Rejection Reason
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }} color="error">
                      {selectedBudget.admin_rejection_comment}
                    </Typography>
                  </Grid>
                )}
              </Grid>

              {/* Action Buttons */}
              <Box
                sx={{
                  mt: 3,
                  display: "flex",
                  gap: 2,
                  justifyContent: "flex-end",
                }}
              >
                {selectedBudget.status === "draft" && (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<SaveIcon />}
                    onClick={() => {
                      setSelectedBudget(null);
                      handleSubmitBudget(selectedBudget);
                    }}
                  >
                    Submit Budget
                  </Button>
                )}

                {(selectedBudget.status === "draft" ||
                  selectedBudget.status === "finance_rejected" ||
                  selectedBudget.status === "admin_rejected") && (
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => {
                      setSelectedBudget(null);
                      handleEditBudget(selectedBudget);
                    }}
                  >
                    Edit Budget
                  </Button>
                )}

                {(selectedBudget.status === "finance_rejected" ||
                  selectedBudget.status === "admin_rejected") && (
                  <Button
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    onClick={() => {
                      setSelectedBudget(null);
                      handleResubmitBudget(selectedBudget);
                    }}
                  >
                    Resubmit Budget
                  </Button>
                )}

                {selectedBudget.status === "draft" && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => {
                      setSelectedBudget(null);
                      handleDeleteBudget(selectedBudget);
                    }}
                  >
                    Delete Budget
                  </Button>
                )}
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Budget Dialog */}
      <Dialog
        open={editMode && !!selectedBudget}
        onClose={() => {
          setEditMode(false);
          setSelectedBudget(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">Edit Budget</Typography>
            <Button
              onClick={() => {
                setEditMode(false);
                setSelectedBudget(null);
              }}
            >
              Cancel
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            Edit your budget details. You can modify the activity name,
            description, and amount. The budget period cannot be changed once
            created.
          </Alert>

          {errors.submit && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {errors.submit}
            </Alert>
          )}

          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {renderStepContent(activeStep)}

          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
            <Button disabled={activeStep === 0} onClick={handleBack}>
              Back
            </Button>

            <Box>
              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading ? "Saving..." : "Save Changes"}
                </Button>
              ) : (
                <Button variant="contained" onClick={handleNext}>
                  Next
                </Button>
              )}
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default BudgetManagementPage;
